import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  type PointerEvent as ReactPointerEvent,
} from 'react';

import { useSankeyLayout } from './use-sankey-layout.js';
import { useGraphSize } from '../../core/components/chart-layout/hooks/useGraphSize.js';
import type { Overlay } from '../../core/components/overlay-tooltip/types.js';
import type { Point } from '../../core/types/point.js';
import { throttleRaf } from '../../core/utils/throttleRaf.js';
import { useClearHoveredShape, useSetHoveredShape } from '../store/store.js';
import type { SankeyHoveredShape } from '../types/sankey-hover.js';
import { findHoveredShape } from '../utils/find-hovered-shape.js';

export function isSameHoveredShape(
  prev: SankeyHoveredShape | undefined,
  next: SankeyHoveredShape | undefined,
): boolean {
  if (prev === next) {
    return true;
  }
  if (prev === undefined || next === undefined) {
    return false;
  }
  if (prev.type === 'node' && next.type === 'node') {
    return prev.node === next.node;
  }
  if (prev.type === 'link' && next.type === 'link') {
    return prev.link === next.link;
  }
  return false;
}

export function useSankeyOverlayEvents(overlay: Overlay) {
  const layout = useSankeyLayout();
  const setHoveredShape = useSetHoveredShape();
  const clearHoveredShape = useClearHoveredShape();
  const { width, height } = useGraphSize();

  // Written synchronously on every pointer move, read by the rAF-throttled
  // flush below, so the flush always hit-tests the latest position instead of
  // the position from whichever call happened to start the frame.
  const latestPointRef = useRef({ clientX: 0, clientY: 0 });
  const lastHoveredRef = useRef<SankeyHoveredShape | undefined>(undefined);

  const publishHoveredShape = useCallback(
    (shape: SankeyHoveredShape | undefined) => {
      if (isSameHoveredShape(lastHoveredRef.current, shape)) {
        return;
      }
      lastHoveredRef.current = shape;
      if (shape) {
        setHoveredShape(shape);
      } else {
        clearHoveredShape();
      }
    },
    [setHoveredShape, clearHoveredShape],
  );

  const hitTest = useCallback(() => {
    const { clientX, clientY } = latestPointRef.current;
    const { relativeX, relativeY } = overlay.getAdjustedPositionsFromAbsolute(
      clientX,
      clientY,
    );
    const point: Point = { x: relativeX ?? 0, y: relativeY ?? 0 };
    publishHoveredShape(findHoveredShape(layout, point));
  }, [overlay, layout, publishHoveredShape]);

  // Recreated only when `hitTest` itself changes identity (i.e. the layout
  // recomputes), so the throttling isn't defeated by unrelated re-renders.
  const throttledHitTest = useMemo(() => throttleRaf<void>(hitTest), [hitTest]);

  const onPointerMove = useCallback(
    (event: ReactPointerEvent<HTMLDivElement>) => {
      latestPointRef.current = {
        clientX: event.clientX,
        clientY: event.clientY,
      };
      throttledHitTest();
    },
    [throttledHitTest],
  );

  const onPointerLeave = useCallback(() => {
    publishHoveredShape(undefined);
    overlay.clear();
  }, [publishHoveredShape, overlay]);

  // Nothing else invalidates the overlay's cached bounding rect here (unlike
  // honeycomb, where the tooltip reducer does it): a resize must still clear
  // it, or stale bounds would silently mis-map future pointer positions.
  useEffect(() => {
    overlay.clear();
  }, [overlay, width, height]);

  return { onPointerMove, onPointerLeave };
}
